import { WEEKDAY_LABELS, WEEKDAYS, localDateKey, type OpeningHoursSchedule, type PortalViewOutput, type Weekday } from '@garage/shared';
import { buttonClasses } from '../../ui/Button.js';
import { cx } from '../../ui/cx.js';
import { MessageIcon, PhoneIcon } from '../../ui/icons.js';
import { RouteIcon } from '../../garage/ui/icons-v7.js';
import { telHref } from './GarageContact.js';

type Garage = PortalViewOutput['garage'];

/** « 8 h 30 », « 18 h » à partir de `HH:MM`. */
export function frTime(hhmm: string): string {
  const [h, m] = hhmm.split(':');
  return m === '00' ? `${Number(h)} h` : `${Number(h)} h ${m}`;
}

function timeIn(iso: string, tz: string): string {
  try {
    const p = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: tz }).formatToParts(new Date(iso));
    return frTime(`${p.find((x) => x.type === 'hour')?.value ?? '00'}:${p.find((x) => x.type === 'minute')?.value ?? '00'}`);
  } catch {
    return '';
  }
}

/**
 * État d'ouverture calculé par le serveur (`openNow`, `closesAt`, `opensAt`, fuseau du garage) mis en
 * phrase : « Ouvert jusqu'à 18 h 30 », « Fermé · ouvre à 14 h », « Fermé · ouvre demain à 8 h 30 »,
 * « Fermé · ouvre lundi à 8 h 30 ». `null` sans horaires structurés.
 */
export function openingSentence(g: Pick<Garage, 'openNow' | 'opensAt' | 'closesAt'>, tz: string, now = new Date()): { open: boolean; text: string } | null {
  if (g.openNow === true) return { open: true, text: g.closesAt ? `Ouvert jusqu’à ${timeIn(g.closesAt, tz)}` : 'Ouvert' };
  if (g.openNow === false) {
    if (!g.opensAt) return { open: false, text: 'Fermé' };
    const day = localDateKey(new Date(g.opensAt), tz);
    const today = localDateKey(now, tz);
    const tomorrow = localDateKey(new Date(now.getTime() + 86_400_000), tz);
    const at = timeIn(g.opensAt, tz);
    if (day === today) return { open: false, text: `Fermé · ouvre à ${at}` };
    if (day === tomorrow) return { open: false, text: `Fermé · ouvre demain à ${at}` };
    const weekday = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', timeZone: tz }).format(new Date(g.opensAt));
    return { open: false, text: `Fermé · ouvre ${weekday} à ${at}` };
  }
  return null;
}

/** Jours consécutifs aux mêmes plages regroupés : « Lundi – vendredi : 8 h 30 – 12 h 30, 14 h – 18 h 30 ». */
export function scheduleLines(schedule: OpeningHoursSchedule): { days: string; hours: string }[] {
  const lines: { from: Weekday; to: Weekday; hours: string }[] = [];
  for (const day of WEEKDAYS) {
    const entry = schedule.days.find((d) => d.day === day);
    const hours = entry ? entry.ranges.map((r) => `${frTime(r.open)} – ${frTime(r.close)}`).join(', ') : 'Fermé';
    const last = lines.at(-1);
    if (last && last.hours === hours) last.to = day;
    else lines.push({ from: day, to: day, hours });
  }
  return lines.map((l) => ({
    days: l.from === l.to ? WEEKDAY_LABELS[l.from] : `${WEEKDAY_LABELS[l.from]} – ${WEEKDAY_LABELS[l.to].toLowerCase()}`,
    hours: l.hours,
  }));
}

/** Vrai si le bloc a quelque chose à montrer (rien n'est écrit en dur : tout vient de la marque du garage). */
export function hasFindUs(g: Garage): boolean {
  return Boolean(g.address || g.phone || g.openingHours || g.mapsUrl || g.openingHoursSchedule);
}

/**
 * Bloc « Nous trouver » du client (vague 7), autonome et réutilisable : état d'ouverture, adresse, gros
 * boutons Itinéraire (lien du serveur, sans clé ni suivi, ouvert seulement si le client le touche), Appeler,
 * Écrire (vers les échanges du suivi, sans toucher au fragment de l'adresse), puis les horaires (structurés, et le texte libre du garage tel quel).
 * Coordonnées, horaires et lien viennent de l'API ; une donnée absente n'est pas affichée.
 */
export function FindUs({ garage, timezone, onWrite, className }: { garage: Garage; timezone: string; onWrite?: (() => void) | null; className?: string }) {
  if (!hasFindUs(garage)) return null;
  const status = openingSentence(garage, timezone);
  const actions = [
    garage.mapsUrl ? (
      <a key="route" href={garage.mapsUrl} target="_blank" rel="noopener noreferrer" className={buttonClasses({ variant: 'primary', block: true })}>
        <RouteIcon size={18} /> Itinéraire
      </a>
    ) : null,
    garage.phone ? (
      <a key="call" href={telHref(garage.phone)} className={buttonClasses({ variant: 'secondary', block: true })}>
        <PhoneIcon size={18} /> Appeler
      </a>
    ) : null,
    // Bouton (pas d'ancre `#…`) : le fragment de l'adresse porte le jeton du client et ne doit pas changer.
    onWrite ? (
      <button key="write" type="button" onClick={onWrite} className={buttonClasses({ variant: 'secondary', block: true })}>
        <MessageIcon size={18} /> Écrire
      </button>
    ) : null,
  ].filter(Boolean);
  return (
    <div className={cx('space-y-4 rounded-2xl border border-line bg-surface p-(--card-p)', className)} data-testid="find-us">
      {status ? (
        <p className={cx('inline-flex items-center gap-2 text-base font-medium', status.open ? 'text-success-fg' : 'text-ink-2')}>
          <span aria-hidden className={cx('size-2 rounded-full', status.open ? 'bg-success' : 'bg-line-strong')} />
          {status.text}
        </p>
      ) : null}
      {garage.address ? <p className="whitespace-pre-line text-ink">{garage.address}</p> : null}
      {actions.length > 0 ? <div className={cx('grid gap-2', actions.length === 3 ? 'sm:grid-cols-3' : actions.length === 2 ? 'sm:grid-cols-2' : '')}>{actions}</div> : null}
      {garage.openingHoursSchedule && garage.openingHoursSchedule.days.length > 0 ? (
        <div>
          <p className="mb-1 text-sm font-medium text-ink-3">Horaires</p>
          <dl className="space-y-0.5 text-base">
            {scheduleLines(garage.openingHoursSchedule).map((l) => (
              <div key={l.days} className="flex flex-wrap justify-between gap-x-4">
                <dt className="text-ink-2">{l.days}</dt>
                <dd className={cx('tabular-nums', l.hours === 'Fermé' ? 'text-ink-3' : 'text-ink')}>{l.hours}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}
      {garage.openingHours ? (
        <div>
          {garage.openingHoursSchedule ? null : <p className="mb-1 text-sm font-medium text-ink-3">Horaires</p>}
          <p className="whitespace-pre-line text-sm text-ink-2">{garage.openingHours}</p>
        </div>
      ) : null}
    </div>
  );
}
